import {
  SecurityAppInvestigations,
  SecurityAppInvestigationsConfig,
  SecurityAppInvestigationsPriorities,
  SecurityAppInvestigationsStatuses
} from "@graylog/enterprise-api";
import { qualifyUrl } from "util/URLUtils";
import EnterpriseApiRoutes from "common/ApiRoutes";
import { stringifyFilters } from "security-app/components/common/Filters/stringifyFilters";
import fetch from "logic/rest/FetchProvider";
import { investigationQueryKey } from "security-app/hooks/queryKeys";
import {
  parseSearchParamsToURLString,
  parseSliceParamsToURLString
} from "security-app/components/common/EntityTableHelpers";
export const searchInvestigationKeyFC = (searchParams) => {
  if (!searchParams) return investigationQueryKey("search");
  return investigationQueryKey(
    "search",
    parseSearchParamsToURLString(searchParams),
    searchParams.sliceCol,
    searchParams.slice
  );
};
export const searchInvestigations = async (searchParams) => {
  const params = parseSearchParamsToURLString(searchParams);
  return fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Investigations.paginated(params).url));
};
export const fetchInvestigationSlices = async (sliceColumn, query, filters) => {
  const params = parseSliceParamsToURLString(sliceColumn, query, filters);
  return fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Investigations.slices(params).url));
};
export const fetchInvestigations = async (page, perPage, query = null, orderBy = void 0, direction = void 0, filters = {}, archived = false, userTimezone = null) => {
  const params = [`page=${page}`, `per_page=${perPage}`, `archived=${archived}`];
  if (query) params.push(`query=${encodeURI(query)}`);
  if (filters && Object.keys(filters).length > 0) {
    params.push(`filters=${encodeURI(stringifyFilters(filters))}`);
    params.push(`user_timezone=${encodeURI(userTimezone)}`);
  }
  if (orderBy) {
    params.push(`sort=${orderBy}`);
  }
  if (direction) {
    params.push(`direction=${direction}`);
  }
  return fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Investigations.index(params.join("&")).url));
};
const investigationToUpdatePayload = (investigation) => ({
  id: investigation.id,
  name: investigation.name,
  assigned_to: investigation.assigned_to,
  priority: investigation.priority,
  status: investigation.status,
  archived: investigation.archived
});
export const updateInvestigations = async ({ investigations }) => {
  const payload = {
    requests: investigations.map(investigationToUpdatePayload)
  };
  return SecurityAppInvestigations.bulkEditInvestigations(payload);
};
export const getInvestigationsDashboardView = async (viewId) => fetch("GET", qualifyUrl(EnterpriseApiRoutes.Dashboards.viewConfig(viewId).url));
export const getInvestigationsDashboardSearch = async (searchId) => fetch("GET", qualifyUrl(EnterpriseApiRoutes.Dashboards.searchJob(searchId).url));
export const getInvestigationsDashboardSearchResults = async ({
  searchId,
  payload
}) => fetch("POST", qualifyUrl(EnterpriseApiRoutes.Dashboards.searchJobResults(searchId).url), payload);
export const getInvestigationDetails = async (invId, includeLogsAndEvents = false) => fetch(
  "GET",
  qualifyUrl(
    EnterpriseApiRoutes.SecurityApp.Investigations.singleInvestigation(
      invId,
      `include_logs_and_events=${includeLogsAndEvents}`
    ).url
  )
);
export const fetchPaginatedInvestigationSecurityEvents = async (invId, page, perPage) => {
  const params = [`page=${page}`, `per_page=${perPage}`];
  return fetch(
    "GET",
    qualifyUrl(
      EnterpriseApiRoutes.SecurityApp.Investigations.paginatedInvestigationSecurityEvents(invId, params.join("&")).url
    )
  );
};
export const fetchPaginatedInvestigationLogs = async (invId, page, perPage) => SecurityAppInvestigations.getLogsPage(invId, page, perPage);
export const archiveInvestigation = async ({ invId, archive }) => SecurityAppInvestigations.archive(invId, archive);
export const bulkArchiveInvestigation = async ({ payload }) => fetch("PUT", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Investigations.bulkArchive().url), payload);
export const deleteInvestigation = async ({ invId }) => SecurityAppInvestigations.deleteInvestigation(invId);
export const bulkDeleteInvestigations = async ({ payload }) => SecurityAppInvestigations.bulkDeleteInvestigations(payload);
export const addEvidence = async ({ invId, payload }) => fetch(
  "PUT",
  qualifyUrl(EnterpriseApiRoutes.SecurityApp.Investigations.addEvidenceToInvestigation(invId).url),
  payload
);
export const bulkAddEvidence = async ({ payload }) => fetch(
  "PUT",
  qualifyUrl(EnterpriseApiRoutes.SecurityApp.Investigations.bulkAddEvidenceToInvestigation().url),
  payload
);
export const removeEvidence = async ({ invId, payload }) => fetch(
  "PUT",
  qualifyUrl(EnterpriseApiRoutes.SecurityApp.Investigations.removeEvidenceFromInvestigation(invId).url),
  payload
);
export const addNote = async ({ invId, note }) => SecurityAppInvestigations.addNewNote(invId, note);
export const editNote = async ({ invId, noteId, note }) => SecurityAppInvestigations.editNote(invId, noteId, note);
export const deleteNote = async ({ invId, noteId }) => SecurityAppInvestigations.deleteNote(invId, noteId);
export const newInvestigation = async ({ payload }) => fetch("POST", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Investigations.new().url), payload);
export const fetchPriorities = async () => SecurityAppInvestigationsPriorities.listPriorities("text", 1, 1e4, void 0, "asc");
export const fetchStatuses = async () => SecurityAppInvestigationsStatuses.listStatuses("status", 1, 1e4, void 0, "asc");
export const fetchUsers = async () => SecurityAppInvestigations.getInvestigationUsers();
export const fetchTeams = async () => SecurityAppInvestigations.getInvestigationTeams();
export const fetchInvestigationsConfig = async () => SecurityAppInvestigationsConfig.getInvestigation();
export const enableNotifications = async ({ enabled }) => SecurityAppInvestigationsConfig.updateConfiguration(enabled);
export const addPriority = async ({ priority }) => SecurityAppInvestigationsPriorities.create(priority);
export const reprioritizePriorities = async ({ payload }) => SecurityAppInvestigationsPriorities.updatePriorities(payload);
export const updatePriority = async ({ priorityId, priority }) => SecurityAppInvestigationsPriorities.update(priorityId, priority);
export const deletePriority = async ({ priorityId }) => SecurityAppInvestigationsPriorities.remove(priorityId);
export const addStatus = async ({ status }) => SecurityAppInvestigationsStatuses.create(status);
export const updateStatus = async ({ statusId, status }) => SecurityAppInvestigationsStatuses.update(statusId, status);
export const deleteStatus = async ({ statusId }) => SecurityAppInvestigationsStatuses.remove(statusId);
export const setDefaultPriority = async ({ priorityId }) => SecurityAppInvestigationsPriorities.setDefault(priorityId);
export const removeDefaultPriority = async () => SecurityAppInvestigationsPriorities.removeDefault();
export const setDefaultStatus = async ({ statusId }) => SecurityAppInvestigationsStatuses.setDefault(statusId);
export const removeDefaultStatus = async () => SecurityAppInvestigationsStatuses.removeDefault();
export const fetchInvestigationProcedures = async (invId, params) => fetch("GET", qualifyUrl(EnterpriseApiRoutes.SecurityApp.Investigations.Procedures.index(invId, params).url));
export const fetchProcedureTitles = async (invId, procedureId, params) => fetch(
  "GET",
  qualifyUrl(EnterpriseApiRoutes.SecurityApp.Investigations.Procedures.titles(invId, procedureId, params).url)
);
export const fetchProcedureStepEvents = async (invId, procedureId, stepId, params) => fetch(
  "GET",
  qualifyUrl(
    EnterpriseApiRoutes.SecurityApp.Investigations.Procedures.steps.events(invId, procedureId, stepId, params).url
  )
);
